import { useAtomValue, useSetAtom } from 'jotai'
import { useNavigate } from 'react-router-dom'
import * as LoginStore from '../stores/LoginStore'
import { pageLinks } from '../../pageLinks'
import IS_FAST_PROTOTYPE_MODE from '../../IS_FAST_PROTOTYPE_MODE.json'

// ============================================================================
// INLINE UI COMPONENTS - self-contained, no external dependencies
// ============================================================================

function Card({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

function CardHeader({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

function CardTitle({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 className={className} {...props}>
      {children}
    </h3>
  )
}

function CardDescription({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`text-sm text-neutral-500 ${className}`} {...props}>
      {children}
    </p>
  )
}

function CardContent({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={className} {...props}>
      {children}
    </div>
  )
}

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'default' | 'outline'
}

function Button({
  className = '',
  variant = 'default',
  children,
  ...props
}: ButtonProps) {
  const variantStyles =
    variant === 'default'
      ? 'bg-neutral-900 text-white'
      : 'bg-neutral-100 text-neutral-900'

  return (
    <button
      className={`inline-flex items-center justify-center text-sm h-10 px-4 py-2 disabled:opacity-50 disabled:pointer-events-none ${variantStyles} ${className}`}
      {...props}
    >
      {children}
    </button>
  )
}

function Input({
  className = '',
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      className={`flex h-10 w-full border-b border-neutral-200 bg-transparent px-0 py-2 text-sm text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:border-neutral-900 disabled:opacity-50 ${className}`}
      {...props}
    />
  )
}

function Label({
  className = '',
  children,
  ...props
}: React.LabelHTMLAttributes<HTMLLabelElement>) {
  return (
    <label
      className={`text-sm text-neutral-500 ${className}`}
      {...props}
    >
      {children}
    </label>
  )
}

interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'destructive'
}

function Alert({
  className = '',
  variant = 'default',
  children,
  ...props
}: AlertProps) {
  const variantStyles =
    variant === 'destructive'
      ? 'text-red-600'
      : 'text-neutral-900'

  return (
    <div
      role="alert"
      className={`text-sm ${variantStyles} ${className}`}
      {...props}
    >
      {children}
    </div>
  )
}

function AlertDescription({
  className = '',
  children,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement>) {
  return (
    <p className={`text-sm ${className}`} {...props}>
      {children}
    </p>
  )
}

function Separator({ className = '' }: { className?: string }) {
  return (
    <div
      role="separator"
      className={`h-px w-full bg-neutral-200 ${className}`}
    />
  )
}

// ============================================================================
// FIELD COMPONENTS - directly connected to Jotai store
// ============================================================================

function EmailField() {
  const { email } = useAtomValue(LoginStore.loginFormAtom)
  const errors = useAtomValue(LoginStore.loginFormErrorsAtom)
  const updateForm = useSetAtom(LoginStore.updateLoginFormAtom)

  return (
    <div className="space-y-1">
      <Label htmlFor="email">Email</Label>
      <Input
        id="email"
        type="email"
        autoComplete="email"
        required
        value={email}
        onChange={(e) => updateForm({ email: e.target.value })}
        placeholder="Enter your email"
        aria-invalid={!!errors?.email}
      />
      {errors?.email && (
        <p className="text-xs text-red-600 mt-1">{errors.email}</p>
      )}
    </div>
  )
}

function PasswordField() {
  const { password } = useAtomValue(LoginStore.loginFormAtom)
  const errors = useAtomValue(LoginStore.loginFormErrorsAtom)
  const updateForm = useSetAtom(LoginStore.updateLoginFormAtom)

  return (
    <div className="space-y-1">
      <Label htmlFor="password">Password</Label>
      <Input
        id="password"
        type="password"
        autoComplete="current-password"
        required
        minLength={8}
        value={password}
        onChange={(e) => updateForm({ password: e.target.value })}
        placeholder="Enter your password"
        aria-invalid={!!errors?.password}
      />
      {errors?.password && (
        <p className="text-xs text-red-600 mt-1">{errors.password}</p>
      )}
    </div>
  )
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Login() {
  const navigate = useNavigate()
  const { isSubmitting } = useAtomValue(LoginStore.loginFormAtom)
  const errors = useAtomValue(LoginStore.loginFormErrorsAtom)
  const submitLogin = useSetAtom(LoginStore.submitLoginAtom)
  const oAuthSignIn = useSetAtom(LoginStore.oAuthSignInAtom)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await submitLogin()
    if (IS_FAST_PROTOTYPE_MODE) {
      navigate(pageLinks.global_shortcut_home())
    } else {
      window.location.href = pageLinks.global_shortcut_home()
    }
  }

  const handleOAuthSignIn = async () => {
    await oAuthSignIn()
  }

  return (
    <div className="min-h-screen bg-white flex flex-col items-center justify-center p-4 sm:p-8">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader className="space-y-1 pb-6 text-center">
            <CardTitle className="text-xl text-neutral-900">
              Welcome back
            </CardTitle>
            <CardDescription>
              Enter your credentials to access your account
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form className="space-y-6" onSubmit={handleSubmit}>
              {errors?.form && (
                <Alert variant="destructive">
                  <AlertDescription>{errors.form}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-4">
                <EmailField />
                <div className="space-y-2">
                  <PasswordField />
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => navigate(pageLinks.RequestPasswordReset())}
                      className="text-xs text-neutral-400 hover:text-neutral-900"
                    >
                      Forgot password?
                    </button>
                  </div>
                </div>
              </div>

              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full"
              >
                {isSubmitting ? 'Signing in...' : 'Sign in'}
              </Button>

              <div className="relative py-3">
                <div className="absolute inset-0 flex items-center">
                  <Separator />
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="bg-white px-4 text-xs text-neutral-400 uppercase">
                    or
                  </span>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                onClick={handleOAuthSignIn}
                className="w-full gap-2"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24">
                  <path
                    fill="currentColor"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="currentColor"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="currentColor"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Google
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="mt-8 text-center text-sm text-neutral-400">
          Don&apos;t have an account?{' '}
          <button
            type="button"
            onClick={() => navigate(pageLinks.Register())}
            className="text-neutral-900 hover:underline"
          >
            Create an account
          </button>
        </p>
      </div>
    </div>
  )
}
